import type { NoteSort, NoteSummary } from '@pm/schemas';
import {
  Button,
  cn,
  EmptyState,
  ErrorState,
  Input,
  LabelCaps,
  PageHeader,
  Select,
  SkeletonRows,
  Tag,
} from '@pm/ui';
import { Lock, NotebookPen, Plus, Search } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { fmtDate } from '@/lib/format';
import { useNoteTags, useNotes } from '@/lib/notes';

const SORT_KEY = 'pm.notes.sort';
const SORTS: { value: NoteSort; label: string }[] = [
  { value: 'updated', label: 'Alteradas recentemente' },
  { value: 'created', label: 'Criadas recentemente' },
  { value: 'title', label: 'Título (A–Z)' },
];
const isSort = (v: unknown): v is NoteSort => SORTS.some((s) => s.value === v);

function storedSort(): NoteSort {
  try {
    const v = localStorage.getItem(SORT_KEY);
    return isSort(v) ? v : 'updated';
  } catch {
    return 'updated';
  }
}

/** Minhas anotações: lista por título, com busca, filtro por tag e ordenação. Só o autor vê. */
export function NotesPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const sortParam = params.get('sort');
  const sort = isSort(sortParam) ? sortParam : storedSort();
  const tag = params.get('tag') ?? undefined;
  const q = params.get('q') ?? '';
  const [text, setText] = useState(q);

  const setParam = (key: string, value: string | undefined) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  // Busca com pequena espera para não consultar a cada tecla.
  useEffect(() => {
    if (text.trim() === q) return;
    const t = setTimeout(() => setParam('q', text.trim() || undefined), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const notes = useNotes({ sort, tag, q: q || undefined });
  const tags = useNoteTags();
  const items = notes.data ?? [];
  const filtered = Boolean(tag || q);
  const newNote = () => void navigate('/anotacoes/nova');

  return (
    <div className="flex max-w-[960px] flex-col gap-gap-lg">
      <PageHeader
        eyebrow={
          <div className="flex items-center gap-1.5">
            <Lock className="size-3.5 text-text-3" />
            <LabelCaps>Privado · só você vê</LabelCaps>
          </div>
        }
        title="Minhas anotações"
        subtitle="Notas em Markdown, organizadas por tags. Nem administradores enxergam."
        actions={
          <Button variant="primary" onClick={newNote}>
            <Plus /> Nova anotação
          </Button>
        }
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          leading={<Search />}
          placeholder="Buscar no título ou no texto"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="Buscar anotações"
          className="flex-1"
        />
        <Select
          aria-label="Ordenar"
          value={sort}
          onChange={(e) => {
            const v = e.target.value as NoteSort;
            try {
              localStorage.setItem(SORT_KEY, v);
            } catch {
              /* preferência só desta sessão */
            }
            setParam('sort', v);
          }}
          className="sm:w-[220px]"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
      </div>

      {tags.data && tags.data.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5" aria-label="Filtrar por tag">
          <FilterChip active={!tag} onClick={() => setParam('tag', undefined)}>
            Todas
          </FilterChip>
          {tags.data.map((t) => (
            <FilterChip
              key={t.tag}
              active={tag === t.tag}
              onClick={() => setParam('tag', tag === t.tag ? undefined : t.tag)}
            >
              {t.tag}
              <span className="font-mono text-xs text-text-3">{t.count}</span>
            </FilterChip>
          ))}
        </div>
      ) : null}

      <div className="rounded-md border border-border bg-surface-1">
        {notes.isLoading ? (
          <SkeletonRows />
        ) : notes.isError ? (
          <ErrorState message={notes.error.message} onRetry={() => void notes.refetch()} />
        ) : items.length === 0 ? (
          filtered ? (
            <EmptyState
              icon={Search}
              title="Nenhuma anotação encontrada"
              description="Tente outro termo ou outra tag."
              action={
                <Button
                  size="sm"
                  onClick={() => {
                    setText('');
                    setParams(sortParam ? { sort: sortParam } : {}, { replace: true });
                  }}
                >
                  Limpar filtros
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={NotebookPen}
              title="Nenhuma anotação ainda"
              description="Guarde ideias, atas e referências em Markdown."
              action={
                <Button variant="primary" size="sm" onClick={newNote}>
                  <Plus /> Criar anotação
                </Button>
              }
            />
          )
        ) : (
          <ul className="divide-y divide-border">
            {items.map((n) => (
              <NoteRow key={n.id} note={n} sort={sort} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-control-sm items-center gap-1.5 rounded border px-2.5 text-sm transition-colors duration-(--dur-fast)',
        active
          ? 'border-accent-border bg-accent-soft text-accent'
          : 'border-border text-text-2 hover:border-border-strong hover:text-text-1',
      )}
    >
      {children}
    </button>
  );
}

const MAX_TAGS_IN_ROW = 3;

function NoteRow({ note, sort }: { note: NoteSummary; sort: NoteSort }) {
  const created = sort === 'created';
  const extra = note.tags.length - MAX_TAGS_IN_ROW;
  return (
    <li>
      <Link
        to={`/anotacoes/${note.id}`}
        className="flex items-start gap-3 px-pad-x py-2.5 transition-colors duration-(--dur-fast) hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-text-1">{note.title}</p>
          {note.preview ? (
            <p className="mt-0.5 truncate text-sm text-text-3">{note.preview}</p>
          ) : null}
          {note.tags.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {note.tags.slice(0, MAX_TAGS_IN_ROW).map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
              {extra > 0 ? <Tag>+{extra}</Tag> : null}
            </div>
          ) : null}
        </div>
        <span
          className="shrink-0 pt-0.5 font-mono text-xs text-text-3"
          title={created ? 'Criada em' : 'Alterada em'}
        >
          {created ? 'criada' : 'alterada'} {fmtDate(created ? note.created_at : note.updated_at)}
        </span>
      </Link>
    </li>
  );
}
